<template>
    <div class="modal fade show add-service-task-modal" aria-labelledby="addNewCardTitle">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content">
                <div class="row">
                    <div class="col-12">
                        <div class="d-flex justify-content-between px-1 my-1 align-item-center">
                            <h1 class="mb-0">Appointment</h1>
                            <div>
                              <button
                                  @click="closeModal"
                                  type="button"
                                  class="btn-close"
                                  data-bs-dismiss="modal"
                                  aria-label="Close">
                              </button>
                            </div>
                        </div>
                    </div>
                </div>

                <hr class="mt-0 mb-0">

                <div class="modal-body row">

                    <div class="col-12 col-sm-12 col-md-12 col-lg-12 mb-1">
                        <label class="form-label">Organization Activity</label>
                        <AsyncSelect
                            multiple
                            placeholder="Select Activity"
                            v-model="formData.activities"
                            :api-service="fetchActivitySetupEndpoint"
                            label="name"
                        />
                    </div>

                    <div class="col-6 col-sm-6 col-md-6 mb-1">
                        <label class="form-label" for="start_time">Start Time</label>
                        <input
                            v-model="formData.start_time"
                            name="start_time"
                            type="datetime-local"
                            class="form-control"
                            placeholder="Enter Start date"
                            @change="onChangeStartTime"
                        />
                    </div>

                    <div class="col-6 col-sm-6 col-md-6 mb-1">
                        <label class="form-label" for="end_time">End Time</label>
                        <input
                            v-model="formData.end_time"
                            name="end_time"
                            type="datetime-local"
                            class="form-control"
                            placeholder="Enter End date"
                        />
                    </div>

                    <div class="col-6 col-sm-6 col-md-6 mb-1">
                        <label class="form-label">Assign To</label>
                        <AsyncSelect
                            placeholder="Assign To"
                            v-model="formData.assign_to"
                            :api-service="fetchContactProfiles"
                            :additional-query="{roles: 'doctor', status: 'active'}"
                        />
                    </div>

                    <div class="col-6 col-sm-6 col-md-6 mb-1">
                        <label class="form-label">Report To</label>
                        <AsyncSelect
                            placeholder="Report To"
                            v-model="formData.report_to"
                            :api-service="fetchContactProfiles"
                            :additional-query="{roles: 'doctor', status: 'active'}"
                            label="name"
                        />
                    </div>

                    <div class="col-12 mb-1">
                        <label for="colFormLabel" class="col-form-label">Participants </label>
                        <AsyncSelect
                            multiple
                            placeholder="Select participants"
                            v-model="formData.participants"
                            :api-service="fetchContactProfiles"
                            :additional-query="{roles: 'doctor', status: 'active'}"
                            label="name"
                        />
                    </div>

                    <div class="col-12 mb-1">
                        <label class="form-label" for="description">Description</label>
                        <div class="input-group input-group-merge w-100">
                            <textarea
                                v-model="formData.description"
                                name="description" rows="3"
                                placeholder="Enter Description"
                                class="form-control"
                            ></textarea>
                        </div>
                    </div>

                    <div class="mb-3 px-2">
                        <label for="attachment" class="form-label fw-bold">Attachment</label>
                        <BlobFileViewer :attachments="attachments" />
                        <FileUpload
                            :is-drag-active="true"
                            button-text="Upload Documents"
                            v-model="attachments"
                        >
                        </FileUpload>
                    </div>

                    <div class="col-12 text-center">
                        <SubmitCancelBtn 
                            @onSubmit="onSubmit"
                            @onCancel="closeModal"
                            :loading="loading"
                        />
                    </div>
        
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {inject, onMounted, ref} from "vue";
import handleContact from '@/services/modules/contact';
import AsyncSelect from "@/components/molecule/input-field/AsyncSelect.vue";
import handleDropDowns from '@/services/modules/dropdowns';
import SubmitCancelBtn from '@/components/atom/SubmitCancelBtn.vue';
import { useStore } from "vuex";
import { getCurrentDateTime } from '@/services/utils/global.js';
import handleDentalOs from '@/services/modules/dental-os/dentalOs';
import { useRoute } from "vue-router";
import { computed } from "vue";
import useDate from '@/services/utils/day';
import FileUpload from "@/components/atom/FileUpload";
import BlobFileViewer from "@/components/atom/BlobFileViewer";

const {fetchContactProfiles} = handleContact();
const { fetchActivitySetupEndpoint } = handleDropDowns();
const { storeTask, updateTask } = handleDentalOs();
const { formatDateTime } = useDate();

const props = defineProps({
    selectedTask: {
        type: Object,
        default: () => null,
    },
    editData: {
        type: Object,
        default: () => null,
    }
});

const emit = defineEmits(['onSaveTask']);

const store = useStore();
const route = useRoute();

const loading = ref(false);
const companyId = computed(() => route.params.companyId);
const showError = inject('showError');
const showSuccess = inject('showSuccess');

const formData = ref({
    start_time: getCurrentDateTime(),
    end_time: getCurrentDateTime(null, { minutes: 10 }),
    assign_to: null,
    report_to: null,
    participants: [],
    activities: [],
    description: '',
    client_id: null,
    sales_order_general_id: null,
    attachments: [],
    previous_file_ids: []
});

const attachments = ref([]);

const onChangeStartTime = (event) => {
    formData.value.end_time = getCurrentDateTime(event.target.value, { minutes: 10 });
}

const onSubmit = async () => {
    const data = formatFormData();

    const action = props.editData ? updateTask : storeTask;
    
    loading.value = true;

    await action(data, props.editData ? props.editData.id : null).then((res) => {
        if (!res.status) return showError(res.message);

        closeModal();
        emit('onSaveTask', res.data);

        return showSuccess(res.message);
    }).catch(err => {
        console.log(err)
    }).finally(() => {
        loading.value = false
    })
}

const formatFormData = () => {

    let previousFileIds = [];

    const data = new FormData();
    data.append('company_id', companyId.value);
    data.append('short_description', props.selectedTask ? props.selectedTask.name : '');
    data.append('start_time', formData.value.start_time ? formatDateTime(formData.value.start_time) : null);
    data.append('end_time', formData.value.end_time ? formatDateTime(formData.value.end_time) : null);
    data.append('assigned_to', formData.value.assign_to ? formData.value.assign_to.id : '');
    data.append('report_to', formData.value.report_to ? formData.value.report_to.id : '');

    formData.value.participants.forEach((item, index) => {
        data.append(`participant_ids[${index}]`, item.id);
    });

    formData.value.activities.forEach((item, index) => {
        data.append(`activity_setup_ids[${index}]`, item.id);
    });

    data.append('full_description', formData.value.description || '');
    data.append('client_id', props.selectedTask ? props.selectedTask.client_id : null);
    data.append('sales_order_general_id', props.selectedTask ? props.selectedTask.id : null);

    for(const key in attachments.value){
        if(typeof attachments.value[key] === 'object' && attachments.value[key].hasOwnProperty('id')) {
            previousFileIds.push(attachments.value[key].id);
            continue;
        }
        data.append('attachments[]', attachments.value[key]);
    }

    data.append('previous_file_ids', JSON.stringify(previousFileIds));

    return data;
}

const closeModal = () => {
    store.state.isModalOpenFour = false;
}

const setInitialData = () => {
    if (props.selectedTask && props.selectedTask.service_resource && !props.editData) {
        formData.value.assign_to = props.selectedTask.service_resource;
    }

    if (props.editData) {
        formData.value.assign_to = props.editData.log_values.assigned_to || null;
        formData.value.report_to = props.editData.log_values.report_to || null;
        formData.value.participants = props.editData.log_values.participants || [];
        formData.value.activities = props.editData.log_values.activities || [];
        formData.value.description = props.editData.full_description || null;
        formData.value.start_time = props.editData.start_time ? getCurrentDateTime(props.editData.start_time) : null;
        formData.value.end_time = props.editData.end_time ? getCurrentDateTime(props.editData.end_time) : null;
        attachments.value = props.editData.log_values.attachments || [];
    }
}

onMounted(() => {
    setInitialData();
});

</script>

<style scoped>
.add-service-task-modal {
    display: block; 
    background: #1c1d1c33
}
</style>